// =============================================================================
// SimSense Platform — Formatters
// =============================================================================

/**
 * Formats a duration in seconds into M:SS.mmm format.
 * @param seconds Duration in seconds
 */
export function formatLapTime(seconds: number | null | undefined): string {
  if (seconds == null || seconds <= 0 || !isFinite(seconds)) return "--:--.---";
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(3);
  return `${mins}:${secs.padStart(6, "0")}`;
}

/**
 * Formats a duration in milliseconds into M:SS.mmm format.
 * @param ms Duration in milliseconds
 */
export function formatLapTimeMs(ms: number | null | undefined): string {
  if (ms == null || ms <= 0 || !isFinite(ms)) return "--:--.---";
  return formatLapTime(ms / 1000);
}

/**
 * Formats temperature based on unit preference.
 * Input can be a string with units (e.g. "20.56 C") or a raw number (assumed F).
 */
export function formatTemp(val: string | number | null | undefined, units: "metric" | "standard" = "standard"): string {
  if (val == null || val === "") return "—";
  
  let num: number;
  let inputUnit: "C" | "F" = "F";

  if (typeof val === "string") {
    const s = val.trim().toUpperCase();
    num = parseFloat(s);
    if (s.endsWith("C")) inputUnit = "C";
    else if (s.endsWith("F")) inputUnit = "F";
  } else {
    num = val;
  }

  if (isNaN(num)) return "—";

  if (units === "metric") {
    // Convert to C if input was F
    const c = inputUnit === "F" ? (num - 32) * 5 / 9 : num;
    return `${Math.round(c)}°C`;
  } else {
    // Convert to F if input was C
    const f = inputUnit === "C" ? (num * 9 / 5) + 32 : num;
    return `${Math.round(f)}°F`;
  }
}

/**
 * Formats speed based on unit preference.
 * Input can be a string with units (e.g. "9.66 km/h") or a raw number (assumed mph).
 */
export function formatSpeed(val: string | number | null | undefined, units: "metric" | "standard" = "standard"): string {
  if (val == null || val === "") return "—";
  
  let num: number;
  let inputUnit: "km/h" | "mph" = "mph";

  if (typeof val === "string") {
    const s = val.trim().toLowerCase();
    num = parseFloat(s);
    if (s.includes("km/h")) inputUnit = "km/h";
    else if (s.includes("mph")) inputUnit = "mph";
  } else {
    num = val;
  }

  if (isNaN(num)) return "—";

  if (units === "metric") {
    // Convert to km/h if input was mph
    const kmh = inputUnit === "mph" ? num * 1.60934 : num;
    return `${Math.round(kmh)} km/h`;
  } else {
    // Convert to mph if input was km/h
    const mph = inputUnit === "km/h" ? num / 1.60934 : num;
    return `${Math.round(mph)} mph`;
  }
}

/**
 * Formats fuel volume based on unit preference.
 * Input is assumed to be Liters (standard iRacing telemetry unit).
 */
export function formatFuel(liters: number | null | undefined, units: "metric" | "standard" = "standard"): string {
  if (liters == null || liters <= 0 || !isFinite(liters)) return "—";
  
  if (units === "metric") {
    return `${liters.toFixed(2)}L`;
  } else {
    const gal = liters * 0.264172;
    return `${gal.toFixed(2)}g`;
  }
}
